<template>
  <div
    class="period-cell"
    :class="{ 'period-cell--drop-target': isDropTarget, 'capacity-highlight-flash': isRecentlyImported, 'period-cell--matched': matched }"
    @dragover="onDragOver"
    @drop="onDrop"
  >
    <div v-if="dayDividers.length" class="period-cell__grid" aria-hidden="true">
      <div v-for="divider in dayDividers" :key="divider" class="period-cell__day-divider" :style="{ left: `${(divider / columnSpan) * FULL_PERCENT}%` }" />
    </div>
    <span v-if="matched" class="period-cell__matched-badge">SAME</span>
    <div v-if="pending" class="period-cell__pending" :aria-label="pendingAriaLabel">
      <button
        type="button"
        class="period-cell__pending-exclude"
        title="Remove from this import"
        aria-label="Remove from this import"
        :disabled="excludeDisabled"
        @click.stop="emit('excludeCell', pending.rowId, pending.weekStart)"
      >
        ×
      </button>
      <span
        class="period-cell__pending-badge"
        :class="{ 'period-cell__pending-badge--decrease': isPendingDecrease }"
        :title="isNettedRollupDecrease ? 'At least one week in this month loses capacity — switch to week view to see which' : undefined"
      >
        {{ pending.kind === 'new' ? 'NEW' : isPendingDecrease ? 'DECREASE' : 'PENDING' }}
      </span>
      <div class="period-cell__pending-delta">
        <template v-if="pending.kind === 'changed'">
          <span class="period-cell__pending-old">{{ formatPendingValue(pending.currentCapacity) }}</span>
          <span class="period-cell__pending-arrow" aria-hidden="true">→</span>
        </template>
        <span class="period-cell__pending-new" :class="{ 'period-cell__pending-new--decrease': isPendingDecrease }">{{ formatPendingValue(pending.nextCapacity) }}</span>
      </div>
      <div v-if="pending.kind === 'new'" class="period-cell__pending-footer">was: no plan</div>
    </div>
    <CapacityAllocationBar
      v-else-if="allocation"
      :purchase-orders="allocation.purchaseOrders"
      :remained-capacity="allocation.remainedCapacity"
      :selected="selected"
      :week-key="weekKey"
      :type-code="typeCode"
      :row-id="rowId"
      :vendor-id="vendorId"
      :capacity-type-id="capacityTypeId"
      :unit="unit"
      :highlighted-segment-key="highlightedSegmentKey"
      :highlighted-anchor-week-key="highlightedAnchorWeekKey"
      :order-keys="orderKeys"
      @select-allocation="emit('selectAllocation')"
      @select-po="(poNumber) => emit('selectPo', poNumber)"
      @select-manual="(assignmentId) => emit('selectManual', assignmentId)"
    />
    <NoPlanCellMenu
      v-else-if="menuOpen"
      :unit="unit"
      :previous-week-plan="previousWeekPlan ?? null"
      @copy="emit('copyPreviousWeek')"
      @add="(capacity) => emit('addCapacity', capacity)"
      @close="emit('closeMenu')"
    />
    <button v-else-if="editable" type="button" class="period-cell__no-plan" title="Create an allocation for this week" @click="emit('createAllocation')">
      <span class="period-cell__no-plan-label">No plan</span>
      <span class="period-cell__no-plan-action" aria-hidden="true">+ add</span>
    </button>
    <span v-else class="period-cell__no-plan period-cell__no-plan--static">
      <span class="period-cell__no-plan-label">No plan</span>
    </span>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';

  import CapacityAllocationBar from '@/components/capacity/CapacityAllocationBar.vue';
  import NoPlanCellMenu from '@/components/capacity/NoPlanCellMenu.vue';
  import { useCalendarDragDrop, type DropResolution } from '@/composables/useCalendarDragDrop';
  import { useRecentCellHighlight } from '@/composables/useRecentCellHighlight';
  import type { WeekCapacityAllocation } from '@/types/capacityCalendar';
  import { formatCapacityQuantity } from '@/utils/capacityCalendar';
  import { isDecrease, type PendingCell } from '@/utils/importPreview';
  import type { PreviousWeekPlan } from '@/utils/previousWeek';

  const FULL_PERCENT = 100;

  const props = defineProps<{
    allocation: WeekCapacityAllocation | null;
    columnSpan: number;
    selected?: boolean;
    // The bucket key for this cell — only meaningful at week granularity;
    // passed through so CapacityAllocationBar can match it against
    // calendarStore.recentAssignments for the flash/dim highlight, and used
    // here as the drop candidate's weekKey.
    weekKey?: string;
    // The row's type code; the bar's tooltip marks this group member as "(this row)".
    typeCode?: string;
    // Drop-target identity — only set at week granularity (see CapacityCalendar.vue);
    // undefined for any row that isn't linked to a portal vendor/capacity type.
    rowId?: string;
    vendorId?: string;
    capacityTypeId?: string;
    // The row's capacity unit (e.g. "parts") — null/undefined for termination rows.
    unit?: string | null;
    // Passed straight through to CapacityAllocationBar — see its prop doc. Forwarded
    // unconditionally (not gated on allocation/pending/etc.) since the bar itself no-ops
    // when there's no allocation rendered.
    highlightedSegmentKey?: string | null;
    // Also passed straight through: the week the selection was made in, so only that week's
    // bar rings and the same block elsewhere reads as related rather than as another selection.
    highlightedAnchorWeekKey?: string | null;
    // Passed through to CapacityAllocationBar: the segment order every bar in this column
    // shares, so one block keeps the same rank down the whole week.
    orderKeys?: string[];
    // Import-preview overlay for this cell (see buildImportDiff) — when set, the ghost
    // treatment renders INSTEAD of the bar/no-plan content and the cell goes inert
    // (no drag, no drop, no click-through to the drawer or the no-plan menu).
    pending?: PendingCell | null;
    // Import-preview "already matches the calendar" marker (see MatchedCell) — unlike `pending`
    // this does NOT replace the cell's content or disable interactivity: the allocation bar (or
    // no-plan button) still renders and clicks/drag-drop still work exactly as they would
    // without an active preview. Only adds a quiet neutral ring + top-right "SAME" badge so a
    // fully-matching file still shows its footprint. Mutually exclusive with `pending` by
    // construction — see CapacityCalendar.vue's `matched` computation.
    matched?: boolean;
    // True while an approve confirm is in flight — disables the × exclude button so it can't
    // mutate a preview that's about to be cleared (or replaced) out from under the in-flight
    // request (see capacityCalendarStore's approvePendingImport race guard).
    excludeDisabled?: boolean;
    // True when clicking this no-plan cell does something — opens the micro-menu (week view +
    // write:capacity) or drills into week view (month rollup). Without it the no-plan cell
    // renders display-only: no hover hint, no title, not focusable (see CapacityCalendar.vue).
    editable?: boolean;
    // Externally controlled — true while this no-plan cell's micro-menu should render.
    menuOpen?: boolean;
    // Resolved by the parent via resolvePreviousWeekPlan; only meaningful while menuOpen.
    previousWeekPlan?: PreviousWeekPlan | null;
  }>();

  const emit = defineEmits<{
    selectAllocation: [];
    selectPo: [poNumber: string];
    selectManual: [assignmentId: string];
    createAllocation: [];
    moveDropped: [resolution: DropResolution];
    closeMenu: [];
    copyPreviousWeek: [];
    addCapacity: [capacity: number];
    excludeCell: [rowId: string, weekStart: string];
  }>();

  const dayDividers = computed(() => Array.from({ length: Math.max(props.columnSpan - 1, 0) }, (_, index) => index + 1));

  function formatPendingValue(value: number | null): string {
    return value === null ? '—' : formatCapacityQuantity(value, props.unit ?? null);
  }

  // Decreases get their own aria copy (capacity reduced, with both values) so a screen-reader
  // user gets the same warning a sighted user gets from the red DECREASE badge.
  const isPendingDecrease = computed(() => (props.pending ? isDecrease(props.pending) : false));

  // Month rollup flagged by a constituent week while its own summed old → new still rises.
  const isNettedRollupDecrease = computed(
    () => props.pending?.containsDecrease === true && props.pending.currentCapacity !== null && props.pending.nextCapacity >= props.pending.currentCapacity
  );

  const pendingAriaLabel = computed(() => {
    if (!props.pending) return undefined;
    const next = formatPendingValue(props.pending.nextCapacity);
    if (props.pending.kind === 'new') return `Pending import: new allocation of ${next}`;
    if (isPendingDecrease.value) {
      const previous = formatPendingValue(props.pending.currentCapacity);
      // A month rollup is flagged when any of its weeks drops, so its own summed totals can still
      // rise — saying "reduced from 200,000 to 210,000" there would be plainly false.
      if (isNettedRollupDecrease.value) return `Pending import: ${previous} to ${next} overall, but at least one week is reduced`;
      return `Pending import: capacity reduced from ${previous} to ${next}`;
    }
    return `Pending import: changes to ${next}`;
  });

  const { isRecentCell } = useRecentCellHighlight();
  const isRecentlyImported = computed(() => isRecentCell(props.rowId, props.weekKey));

  const { activePayload, isCompatibleTarget, completeDrop } = useCalendarDragDrop();

  // A pending (ghost) cell is inert — never a drag source (no bar rendered) and never a
  // drop target either, even for a row whose rowId/vendorId happen to be set.
  const isDropTargetEnabled = computed(() => !props.pending && Boolean(props.rowId && props.vendorId && props.capacityTypeId && props.weekKey));

  const dropCandidate = computed(() => {
    if (!isDropTargetEnabled.value) {
      return null;
    }
    return { rowId: props.rowId!, vendorId: props.vendorId!, capacityTypeId: props.capacityTypeId!, weekKey: props.weekKey! };
  });

  const isDropTarget = computed(() => (dropCandidate.value && activePayload.value ? isCompatibleTarget(dropCandidate.value) : false));

  function onDragOver(event: DragEvent): void {
    if (!isDropTarget.value) {
      return;
    }
    // Only allow the drop (and show the "move" cursor) over a compatible target.
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  }

  function onDrop(event: DragEvent): void {
    if (!dropCandidate.value) {
      return;
    }
    event.preventDefault();
    const resolution = completeDrop(dropCandidate.value);
    if (resolution) {
      emit('moveDropped', resolution);
    }
  }
</script>

<style scoped>
  .period-cell {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
    min-height: 4rem;
  }

  /* Shown on every compatible drop target for the duration of an active drag
     (see useCalendarDragDrop) — not just the one currently under the pointer. */
  .period-cell--drop-target {
    z-index: 1;
    outline: 2px dashed rgb(37 99 235);
    outline-offset: -2px;
    background: rgb(219 234 254 / 55%);
  }

  /* Import-preview "already matches" marker (see MatchedCell) — a quiet neutral ring, unlike
     the pending overlay's dashed amber outline+stripes: this cell's normal content (allocation
     bar / no-plan button) still renders underneath and stays fully interactive. inset box-shadow
     rather than outline/border so it doesn't affect layout or fight period-cell--drop-target's
     own outline. */
  .period-cell--matched {
    box-shadow: inset 0 0 0 1.5px rgb(148 163 184); /* slate-300 */
    border-radius: 0.375rem;
  }

  /* Mirrors .period-cell__pending-badge's geometry (top-right tab) in neutral gray. Positioned
     against .period-cell itself (not a nested overlay) since the matched cell's own content is
     still the thing rendering underneath — pointer-events: none keeps it from ever intercepting
     the click/drag that content still needs to handle. */
  .period-cell__matched-badge {
    position: absolute;
    top: -0.5rem;
    right: -0.375rem;
    z-index: 2;
    padding: 0.0625rem 0.375rem;
    border-radius: 0.25rem;
    background: rgb(107 114 128); /* gray-500 */
    color: white;
    font-size: 0.5625rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    pointer-events: none;
  }

  /* Spans the td's border box (undoing the cell's 0.5rem/0.75rem padding) so
     percentage-placed dividers line up with the header's column borders. */
  .period-cell__grid {
    position: absolute;
    z-index: 4;
    inset: -0.5rem -0.75rem;
    pointer-events: none;
  }

  .period-cell__day-divider {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 1px;
    background: rgb(203 213 225);
  }

  /* Import preview overlay (spec §3): dashed amber outline + diagonal-striped light-amber
     fill, top-right amber badge tab, centered old→new delta. pointer-events: none is what
     makes the cell inert — no hover/click reaches a drag handle or the no-plan button
     underneath (there isn't one; this branch replaces it entirely). */
  .period-cell__pending {
    position: relative;
    display: flex;
    width: 100%;
    height: 100%;
    min-height: 4rem;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.125rem;
    padding: 0.375rem;
    border: 2px dashed #f59e0b;
    border-radius: 0.375rem;
    background-color: rgb(254 243 199 / 55%);
    background-image: repeating-linear-gradient(135deg, transparent, transparent 6px, rgb(245 158 11 / 14%) 6px, rgb(245 158 11 / 14%) 12px);
    text-align: center;
    pointer-events: none;
  }

  .period-cell__pending-badge {
    position: absolute;
    top: -0.5rem;
    right: -0.375rem;
    padding: 0.0625rem 0.375rem;
    border-radius: 0.25rem;
    background: #f59e0b;
    color: white;
    font-size: 0.5625rem;
    font-weight: 700;
    letter-spacing: 0.02em;
  }

  /* A cut, not a raise — the badge and new value break from the shared amber treatment (outline
     and stripes stay amber for every pending cell) so a decrease reads distinctly at a glance. */
  .period-cell__pending-badge--decrease {
    background: #dc2626;
  }

  /* Only interactive part of an otherwise inert (pointer-events: none) pending cell — lets the
     user drop this one cell from the preview without the click reaching drag/drop handlers. */
  .period-cell__pending-exclude {
    position: absolute;
    top: -0.5rem;
    left: -0.375rem;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.125rem;
    height: 1.125rem;
    border: 1px solid rgb(217 119 6);
    border-radius: 9999px;
    background: white;
    color: rgb(146 64 14);
    font-size: 0.75rem;
    line-height: 1;
    cursor: pointer;
    pointer-events: auto;
  }

  .period-cell__pending-exclude:hover:not(:disabled) {
    background: rgb(254 243 199);
  }

  .period-cell__pending-exclude:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  .period-cell__pending-delta {
    display: flex;
    align-items: baseline;
    gap: 0.25rem;
    font-size: 0.75rem;
    font-weight: 700;
  }

  .period-cell__pending-old {
    color: rgb(148 163 184);
    font-weight: 500;
    text-decoration: line-through;
  }

  .period-cell__pending-arrow {
    color: rgb(180 83 9);
  }

  .period-cell__pending-new {
    color: rgb(120 53 15);
    font-weight: 700;
  }

  .period-cell__pending-new--decrease {
    color: rgb(153 27 27);
  }

  .period-cell__pending-footer {
    font-size: 0.625rem;
    font-style: italic;
    color: rgb(146 64 14);
  }

  .period-cell__no-plan {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    width: 100%;
    height: 100%;
    padding: 0;
    border: 0;
    background: transparent;
    color: rgb(148 163 184);
    cursor: pointer;
    font-size: 0.6875rem;
    text-align: left;
  }

  .period-cell__no-plan-label {
    font-style: italic;
  }

  .period-cell__no-plan-action {
    color: rgb(37 99 235);
    font-weight: 600;
    opacity: 0;
    transition: opacity 120ms ease;
  }

  .period-cell__no-plan:hover .period-cell__no-plan-action,
  .period-cell__no-plan:focus-visible .period-cell__no-plan-action {
    opacity: 1;
  }

  .period-cell__no-plan:focus-visible {
    outline: 2px solid rgb(37 99 235);
    outline-offset: -2px;
    border-radius: 0.375rem;
  }

  /* Non-editable no-plan cell (Day/Month granularity, or no write:capacity): display-only,
     matching the interactive version's layout but with no hover hint and no pointer cursor —
     there is nothing to click here. */
  .period-cell__no-plan--static {
    cursor: default;
  }
</style>
